/**
 * Lightweight journey progress (localStorage) until interview/scheduling APIs are complete.
 * Keys are scoped by user id when available.
 */

const candidateKey = (userId) => `ewu_candidate_journey_${userId || 'anon'}`;
const consultancyKey = (userId) => `ewu_consultancy_journey_${userId || 'anon'}`;
const schedulesKey = (userId) => `ewu_interview_schedules_${userId || 'anon'}`;
const pipelineKey = (userId) => `ewu_candidate_pipeline_${userId || 'anon'}`;

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : { ...fallback };
  } catch {
    return { ...fallback };
  }
};

const write = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
  return data;
};

const DEFAULT_CANDIDATE = {
  domainSelected: null,
  modeSelected: null,
  firstMockStarted: false,
  firstMockCompleted: false,
  lastScore: null,
  discoverableHintSeen: false,
  jdPlanCreated: false,
};

const DEFAULT_CONSULTANCY = {
  companyAdded: false,
  requirementAdded: false,
  candidateInvited: false,
  talentSearched: false,
  firstUnlock: false,
  packSubscribed: false,
};

export const getCandidateJourney = (userId) => read(candidateKey(userId), DEFAULT_CANDIDATE);

export const updateCandidateJourney = (userId, patch) => {
  const next = { ...getCandidateJourney(userId), ...patch, updatedAt: Date.now() };
  return write(candidateKey(userId), next);
};

export const getConsultancyJourney = (userId) =>
  read(consultancyKey(userId), DEFAULT_CONSULTANCY);

export const updateConsultancyJourney = (userId, patch) => {
  const next = { ...getConsultancyJourney(userId), ...patch, updatedAt: Date.now() };
  return write(consultancyKey(userId), next);
};

export const PIPELINE_STAGES = [
  'sourced',
  'contacted',
  'interviewing',
  'offered',
  'placed',
];

export const getPipelineMap = (userId) => read(pipelineKey(userId), {});

export const setCandidatePipelineStage = (userId, candidateId, stage) => {
  const map = getPipelineMap(userId);
  map[String(candidateId)] = stage;
  return write(pipelineKey(userId), map);
};

export const getSchedules = (userId) => {
  try {
    const raw = localStorage.getItem(schedulesKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveSchedules = (userId, list) => {
  write(schedulesKey(userId), list);
  return list;
};

export const addSchedule = (userId, interview) => {
  const list = getSchedules(userId);
  const entry = {
    id: interview.id || `sch_${Date.now()}`,
    status: 'scheduled',
    createdAt: new Date().toISOString(),
    ...interview,
  };
  list.unshift(entry);
  saveSchedules(userId, list);
  return entry;
};

/** Soft-paywall: show upgrade when unlocks remaining hit threshold */
export const shouldShowUnlockPaywall = (entitlement) => {
  if (!entitlement) return false;
  if (entitlement.requires_entitlement) return true;
  if (entitlement.code === 'talent_entitlement_required') return true;
  const remaining = entitlement.unlocks_remaining;
  if (remaining == null) return false;
  return remaining <= 3;
};
